import React from "react";
import {
  Table,
  Button,
  Space,
  message,
  Tooltip,
  Modal,
  Typography,
  Badge,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RiseOutlined,
  FundViewOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  fetchScenarios,
  deleteScenario,
  updateScenario,
} from "../../services/scenarios";
import { useQuery, useMutation } from "@tanstack/react-query";
import Loader from "../../components/Loading";
import {
  priorityLevels,
  queryKeys,
  scenarioTypes,
  statuses,
} from "../../constants";
import classes from "./Scenario.module.scss";
import { useNavigate } from "react-router-dom";
import { getThemeColor } from "../../helpers";

const { Title } = Typography;

const deleteModal = (handleDelete) => {
  Modal.confirm({
    title: "Delete Scenario",
    content: "Are you sure you want to delete this scenario ?",
    okText: "Yes",
    okType: "danger",
    okButtonProps: { type: "primary" },
    cancelText: "No",
    onOk() {
      handleDelete();
    },
  });
};

const ScenarioTable = () => {
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.scenariosList],
    queryFn: fetchScenarios,
    refetchInterval: 1000 * 30, // Refetch every 30 seconds
  });

  const deleteMutation = useMutation({
    mutationFn: deleteScenario,
    onSuccess: () => {
      message.success("Scenario deleted successfully");
      refetch();
    },
    onError: (error) => {
      message.error(`Error: ${error.message}`);
    },
  });

  const updateScenarioMutation = useMutation({
    mutationFn: updateScenario,
    onSuccess: () => {
      message.success("Mitigation plan regenerated successfully");
      refetch();
    },
    onError: (error) => {
      message.error(`Error: ${error.message}`);
    },
  });

  const handleRegenerateMitigation = (record) => {
    // Regenerate mitigation plan
    // This will trigger the AI calculation again
    // and update the mitigation plan
    // This will only be available for scenarios with status AI_FAILED
    updateScenarioMutation.mutate({
      id: record.id,
      updateData: {
        ...record,
        status: "READY_FOR_SUBMISSION",
      },
    });
  };

  // Columns for the table
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 350,
      render: (text, record) => (
        <span
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/home/scenario-details/${record.id}`)}
          title={text}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Current Scenario",
      dataIndex: "current_scenario",
      key: "current_scenario",
      width: 140,
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              textDecoration: "underline",
              color: getThemeColor(),
              cursor: "pointer",
            }}
          >
            View
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Objectives",
      dataIndex: "objectives",
      key: "objectives",
      width: 120,
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              textDecoration: "underline",
              color: getThemeColor(),
              cursor: "pointer",
            }}
          >
            View
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Scenario Type",
      dataIndex: "scenario_type",
      key: "scenario_type",
      width: 120,
      render: (text) => {
        const scenarioType = scenarioTypes.find(
          (scenarioType) => scenarioType.value === text
        );
        return scenarioType ? scenarioType.label : text;
      },
    },
    {
      title: "Budget Estimate",
      dataIndex: "budget_estimate",
      key: "budget_estimate",
      width: 140,
      render: (text) => `$${parseFloat(text).toFixed(2)}`,
    },
    {
      title: "Priority Level",
      dataIndex: "priority_level",
      key: "priority_level",
      width: 140,
      filters: [
        { text: "Critical", value: "CRITICAL" },
        { text: "High", value: "HIGH" },
        { text: "Medium", value: "MEDIUM" },
        { text: "Low", value: "LOW" },
      ],
      onFilter: (value, record) => record.priority_level === value,
      render: (text) => {
        const priority = priorityLevels.find(
          (priority) => priority.value === text
        );
        return priority ? priority.label : text;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Draft", value: "DRAFT" },
        { text: "Approved", value: "APPROVED" },
        { text: "Rejected", value: "REJECTED" },
        { text: "Pending", value: "AI_PENDING" },
        { text: "Completed", value: "AI_COMPLETED" },
        { text: "Failed", value: "AI_FAILED" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => {
        const status = statuses.find((status) => status.value === text);
        return (
          <Badge color={status.color ?? "grey"} text={status.label ?? text} />
        );
      },
      width: 140,
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (record) => (
        <Space size="middle">
          {
            // If the status is AI_COMPLETED, only then the user can calculate risk and opportunity
            record.status === "AI_COMPLETED" && (
              <Tooltip title="Calculate Risk & Opportunity">
                <RiseOutlined
                  style={{ color: getThemeColor(), cursor: "pointer" }}
                  onClick={() => navigate(`/home/scenario-details/${record.id}`)}
                />
              </Tooltip>
            )
          }
          {
            // If the status is AI_COMPLETED, only then the user can generate mitigation plan
            record.is_mitigated && (
              <Tooltip title="View Mitigation Plan">
                <FundViewOutlined
                  style={{ color: getThemeColor(), cursor: "pointer" }}
                  onClick={() => navigate(`/home/mitigation-plan/${record.id}`)}
                />
              </Tooltip>
            )
          }
          {record.status === "AI_FAILED" && (
            <Tooltip title="Regenerate Mitigation">
              <SyncOutlined
                style={{ color: getThemeColor(), cursor: "pointer" }}
                onClick={() => handleRegenerateMitigation(record)}
              />
            </Tooltip>
          )}
          {!record.is_mitigated && ( // If the scenario is not mitigated, then only show the edit button
            <Tooltip title="Edit Objective">
              <EditOutlined
                style={{ color: getThemeColor(), cursor: "pointer" }}
                onClick={() =>
                  navigate("/home/create-scenario?id=" + record.id)
                }
              />
            </Tooltip>
          )}
          <Tooltip title="Delete Objective">
            <DeleteOutlined
              style={{ color: "red", cursor: "pointer" }}
              onClick={() =>
                deleteModal(() => deleteMutation.mutate(record.id))
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className={classes.header}>
        <Row gutter={[8, 8]} align={"middle"}>
          <Col md={12} sm={24}>
            <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
              {" "}
              Objective's Overview List
            </Title>
          </Col>
          <Col md={12} sm={24} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/home/create-scenario")}
            >
              Create Objective
            </Button>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={data?.data?.results ?? []}
        rowKey="id"
        size="small"
        scroll={{ x: 1500 }} // Enable horizontal and vertical scrolling
      />
    </div>
  );
};

export default ScenarioTable;
